import { Select, type RefSelectProps } from 'antd';
import { FC, KeyboardEvent, RefObject } from 'react';

// Common props shared by all selects
interface BaseSelectProps {
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur: () => void;
}

interface Option {
  label: string;
  value: string;
}

export const TagSelect: FC<BaseSelectProps & {
  selectRef: RefObject<RefSelectProps | null>;
  value: string | null;
  onChange: (val: string) => void;
  options: Option[];
  open: boolean;
  onDropdownVisibleChange: (open: boolean) => void;
}> = ({ selectRef, value, onChange, onKeyDown, onBlur, options, open, onDropdownVisibleChange }) => (
  <Select
    ref={selectRef}
    className='xm-search-input__select'
    placeholder='Select tag...'
    value={value}
    onChange={onChange}
    onKeyDown={onKeyDown}
    options={options}
    variant="borderless"
    showSearch={{ filterOption: (input, option) => option?.value.toLowerCase().includes(input.toLowerCase()) ?? false }}
    open={open}
    onOpenChange={onDropdownVisibleChange}
    onBlur={onBlur}
  />
);

export const TagCategorySelects: FC<BaseSelectProps & {
  tagCategorySelectRef: RefObject<RefSelectProps | null>;
  categoryTagSelectRef: RefObject<RefSelectProps | null>;
  selectedTagCategory: string | null;
  selectedCategoryTag: string | null;
  onCategoryChange: (val: string) => void;
  onTagChange: (val: string) => void;
  categoryOptions: Option[];
  tagOptions: Option[];
  categoryOpen: boolean;
  tagOpen: boolean;
  onCategoryDropdownChange: (open: boolean) => void;
  onTagDropdownChange: (open: boolean) => void;
}> = ({
  tagCategorySelectRef, categoryTagSelectRef,
  selectedTagCategory, selectedCategoryTag,
  onCategoryChange, onTagChange,
  categoryOptions, tagOptions,
  categoryOpen, tagOpen,
  onCategoryDropdownChange, onTagDropdownChange,
  onKeyDown, onBlur
}) => (
    <>
      <Select
        ref={tagCategorySelectRef}
        className='xm-search-input__select'
        placeholder='Select category...'
        value={selectedTagCategory}
        onChange={onCategoryChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        options={categoryOptions}
        variant="borderless"
        showSearch
        open={categoryOpen}
        onOpenChange={onCategoryDropdownChange}
      />
      {selectedTagCategory !== null && (
        <Select
          ref={categoryTagSelectRef}
          className='xm-search-input__select'
          placeholder='Select tag...'
          value={selectedCategoryTag}
          onChange={onTagChange}
          options={tagOptions}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          variant="borderless"
          showSearch={{ optionFilterProp: 'label' }}
          open={tagOpen}
          onOpenChange={onTagDropdownChange}
        />
      )}
    </>
  );

export const TechnologySelects: FC<BaseSelectProps & {
  technologySelectRef: RefObject<RefSelectProps | null>;
  subsystemSelectRef: RefObject<RefSelectProps | null>;
  selectedTechnology: string | null;
  selectedSubsystem: string | null;
  onTechnologyChange: (val: string) => void;
  onSubsystemChange: (val: string) => void;
  technologyOptions: Option[];
  subsystemOptions: Option[];
  technologyOpen: boolean;
  subsystemOpen: boolean;
  onTechnologyDropdownChange: (open: boolean) => void;
  onSubsystemDropdownChange: (open: boolean) => void;
}> = ({
  technologySelectRef, subsystemSelectRef,
  selectedTechnology, selectedSubsystem,
  onTechnologyChange, onSubsystemChange,
  technologyOptions, subsystemOptions,
  technologyOpen, subsystemOpen,
  onTechnologyDropdownChange, onSubsystemDropdownChange,
  onKeyDown, onBlur
}) => (
    <>
      <Select
        ref={technologySelectRef}
        className='xm-search-input__select'
        placeholder='Select technology...'
        value={selectedTechnology}
        onChange={onTechnologyChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        options={technologyOptions}
        variant="borderless"
        showSearch={{ optionFilterProp: 'label' }}
        open={technologyOpen}
        onOpenChange={onTechnologyDropdownChange}
      />
      {selectedTechnology !== null && (
        <Select
          ref={subsystemSelectRef}
          className='xm-search-input__select'
          placeholder='Select subsystem...'
          value={selectedSubsystem}
          onChange={onSubsystemChange}
          options={subsystemOptions}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          variant="borderless"
          showSearch={{ optionFilterProp: 'label' }}
          open={subsystemOpen}
          onOpenChange={onSubsystemDropdownChange}
        />
      )}
    </>
  );

export const HealthStatusSelect: FC<BaseSelectProps & {
  healthStatusSelectRef: RefObject<RefSelectProps | null>;
  value: string | null;
  onChange: (val: string) => void;
  options: Option[];
  open: boolean;
  onDropdownVisibleChange: (open: boolean) => void;
}> = ({ healthStatusSelectRef, value, onChange, onKeyDown, onBlur, options, open, onDropdownVisibleChange }) => (
  <Select
    ref={healthStatusSelectRef}
    className='xm-search-input__select'
    placeholder='Select health status...'
    value={value ?? undefined}
    onChange={onChange}
    onKeyDown={onKeyDown}
    onBlur={onBlur}
    options={options}
    variant="borderless"
    showSearch
    open={open}
    onOpenChange={onDropdownVisibleChange}
  />
);
